import { useCallback, useMemo } from 'react';

import { FieldValues } from 'react-hook-form';

import isEqual from 'fast-deep-equal';
import { get } from 'radash';

import { isDefined, isOfType } from '@hsp/utilities';

import { useAppDispatch, useAppSelector } from 'hooks/store';
import { AudienceActions } from 'store/actions';
import {
  selectAudienceDefaultFieldValues,
  selectAudienceFieldValues,
  selectAudienceIsMultiRfm,
  selectAudiencePreviousFieldValues,
} from 'store/selectors';
import { IMultiRfmOperation } from 'types/api/audience';
import { FieldValuesReturnType } from 'types/pages/audience-builder/shared/hooks';

export const useFieldValues = () => {
  const dispatch = useAppDispatch();

  const advanced = useAppSelector(selectAudienceIsMultiRfm);
  const defaultValues = useAppSelector(selectAudienceDefaultFieldValues, isEqual);
  const fieldValues = useAppSelector(selectAudienceFieldValues, isEqual);
  const previousFieldValues = useAppSelector(selectAudiencePreviousFieldValues, isEqual);

  const hasFieldValuesChanged = useMemo(
    () => !isEqual(previousFieldValues, fieldValues),
    [fieldValues, previousFieldValues]
  );

  const isDefault = useMemo(() => isEqual(fieldValues, defaultValues), [defaultValues, fieldValues]);

  const getFieldValue = useCallback(
    <TValue = string>(fieldName: string) => get<TValue>(fieldValues, fieldName),
    [fieldValues]
  );

  const loadFieldValues = useCallback(
    (load?: FieldValues | IMultiRfmOperation) => {
      return new Promise<FieldValuesReturnType>((resolve, reject) => {
        if (isDefined(load)) {
          let fieldValuesToLoad: FieldValues;
          let result: FieldValuesReturnType;

          if (isOfType<IMultiRfmOperation>(load, 'rfmSets')) {
            const { operation, rfmSets } = load;
            const { length } = rfmSets;

            dispatch(AudienceActions.setMultiRfmIndex(length - 1));
            dispatch(AudienceActions.setIsMultiRfm(true));
            dispatch(AudienceActions.setMultiRfmGroups(rfmSets));
            dispatch(AudienceActions.setMultiRfmOperation(operation));

            fieldValuesToLoad = rfmSets[length - 1].fieldValues;
            result = { fieldValues: fieldValuesToLoad, isAdvanced: true, multiRfmOperation: load };
          } else {
            fieldValuesToLoad = load;
            result = { fieldValues: load, isAdvanced: false };
          }

          dispatch(AudienceActions.setFieldValues(fieldValuesToLoad));

          resolve(result);

          return;
        }

        reject();
      });
    },
    [dispatch]
  );

  return {
    advanced,
    defaultValues,
    fieldValues,
    getFieldValue,
    hasFieldValuesChanged,
    isDefault,
    loadFieldValues,
    previousFieldValues,
  } as const;
};
